/**
 * Every candidate enum value's human label, and the chip tone where it has one.
 *
 * One file, so a label cannot say "Details mailed" in the table and "Details Mailed"
 * in the filter dropdown.
 *
 * The order each record is written in **is** the order it appears in a dropdown, so
 * these are in pipeline order rather than alphabetical. Someone scanning a status
 * filter is looking for a stage of a process, not a word beginning with A.
 */

import type {
  ApplyingFor,
  ReferenceRead,
  CandidateSource,
  CandidateStatus,
  DocumentationState,
  FlystStage,
  PaymentStatus,
  RsrState,
} from '@/api/schema';
import type { ChipTone } from '@/components/ui/Chip';

export type Option<T extends string> = { value: T; label: string };

/** Turn a label record into Select options, keeping the record's own order. */
function optionsFrom<T extends string>(labels: Record<T, string>): Option<T>[] {
  return (Object.keys(labels) as T[]).map((value) => ({ value, label: labels[value] }));
}

/**
 * Reads a stored value back into its label, tolerating one that is no longer listed.
 *
 * For the history cards, which show values written in the past. The history outlives
 * the enum: a value retired in a later release still sits in rows written before it
 * went. Falling back to the raw `applicant_screening_done` is poor, and rendering
 * `undefined` is a bug.
 *
 * Widened to `string` on the way in, because that is the honest type: the compiler
 * cannot know what an old row holds, and typing the parameter as the enum would make
 * the fallback look like dead code.
 */
export function labelledBy<T extends string>(labels: Record<T, string>) {
  const lookup: Record<string, string | undefined> = labels;
  return (value: string) => lookup[value] ?? value;
}

// --- overall status -------------------------------------------------------- //

export const STATUS_LABEL: Record<CandidateStatus, string> = {
  candidate_applied: 'Candidate applied',
  details_mailed: 'Details mailed',
  job_details_sent: 'Job details sent',
  applicant_screening_arranged: 'Applicant screening arranged',
  applicant_screening_done: 'Applicant screening done',
  documentation_started: 'Documentation started',
  documentation_done: 'Documentation done',
  rsr_screening_arranged: 'RSR screening arranged',
  approved_for_processing: 'Approved for processing',
  onboarding_in_progress: 'Onboarding in progress',
  hold: 'Hold',
  rejected: 'Rejected',
};

/**
 * Related milestones share the existing process colours; their labels distinguish them.
 */
export const STATUS_TONE: Record<CandidateStatus, ChipTone> = {
  candidate_applied: 'applied',
  details_mailed: 'details-mailed',
  job_details_sent: 'details-mailed',
  applicant_screening_arranged: 'applicant-arranged',
  applicant_screening_done: 'applicant-done',
  documentation_started: 'applicant-arranged',
  documentation_done: 'applicant-done',
  rsr_screening_arranged: 'rsr-arranged',
  approved_for_processing: 'approved',
  onboarding_in_progress: 'onboarding',
  hold: 'hold',
  rejected: 'rejected',
};

// --- Flyst screening ------------------------------------------------------- //

export const FLYST_LABEL: Record<FlystStage, string> = {
  introduction: 'Introduction',
  communication: 'Communication',
  ok_for_processing: 'OK for processing',
  sponsor_screening_arranged: 'Sponsor screening arranged',
};

/**
 * Mapped onto four of the existing status tones rather than given tokens of their own.
 *
 * The prototype's four stage colours are already four of the nine status colours, and
 * "a status colour means the same thing everywhere" (plan 9.2) is easier to keep true
 * with nine tokens than with thirteen.
 */
export const FLYST_TONE: Record<FlystStage, ChipTone> = {
  introduction: 'applied',
  communication: 'details-mailed',
  ok_for_processing: 'approved',
  sponsor_screening_arranged: 'rsr-arranged',
};

// --- RSR screening --------------------------------------------------------- //

export const RSR_LABEL: Record<RsrState, string> = {
  pending: 'Pending',
  arranged: 'Arranged',
  // Derived like the two above it: the booking's date has been and gone and nobody has
  // recorded the outcome yet. Not the same as pending, which means nothing was ever
  // booked. Reading a passed date as pending threw that difference away.
  happened: 'Happened',
  re_screening: 'Re-screening',
  done: 'Done',
  hold: 'Hold',
  rejected: 'Rejected',
};

// --- the rest -------------------------------------------------------------- //

export const DOCUMENTATION_LABEL: Record<DocumentationState, string> = {
  pending: 'Pending',
  done: 'Done',
};

export const SOURCE_LABEL: Record<CandidateSource, string> = {
  linkedin: 'LinkedIn',
  website: 'Website',
  social_media: 'Social media',
  whatsapp_community: 'WhatsApp community',
  email: 'Email',
  call: 'Call',
  referral: 'Referral',
};

export const APPLYING_FOR_LABEL: Record<ApplyingFor, string> = {
  internship: 'Internship',
  placement: 'Placement',
};

export const PAYMENT_LABEL: Record<PaymentStatus, string> = {
  not_applicable: 'Not applicable',
  pending: 'Pending',
  partial: 'Partial',
  paid: 'Paid',
};

/**
 * Money, not process: a different dimension from the candidate status, which is why
 * red here is not a contradiction of plan 9.2.
 *
 * Pending reads as "money outstanding", which is the alarm the office actually wants.
 * The alternative, with the tones available, is two identical amber chips for Partial
 * and Pending, which loses the distinction that matters most.
 */
export const PAYMENT_TONE: Record<PaymentStatus, ChipTone> = {
  not_applicable: 'neutral',
  pending: 'rejected',
  partial: 'hold',
  paid: 'approved',
};

// --- dropdown options ------------------------------------------------------ //

export const STATUS_OPTIONS = optionsFrom(STATUS_LABEL);
export const SOURCE_OPTIONS = optionsFrom(SOURCE_LABEL);
export const APPLYING_FOR_OPTIONS = optionsFrom(APPLYING_FOR_LABEL);
export const FLYST_OPTIONS = optionsFrom(FLYST_LABEL);
export const RSR_OPTIONS = optionsFrom(RSR_LABEL);
export const DOCUMENTATION_OPTIONS = optionsFrom(DOCUMENTATION_LABEL);
export const PAYMENT_OPTIONS = optionsFrom(PAYMENT_LABEL);

/** Sponsor points as a dropdown. Strings, because a `<select>` value is a string. */
export const POINTS_OPTIONS = ['0', '1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5'].map(
  (value) => ({
    value,
    label: value === '0' ? 'Not rated' : value,
  }),
);

/**
 * A reference list as options, keeping a retired entry only when it is the one already
 * selected; otherwise saving a record would silently drop it.
 *
 * `selected` is always empty on the create form, so nothing retired is ever offered
 * there. It matters on the edit form, which is why the parameter exists.
 */
export function referenceOptions(items: ReferenceRead[] | undefined, selected: string) {
  return (items ?? [])
    .filter((item) => item.is_active || item.id === selected)
    .map((item) => ({ value: item.id, label: item.name }));
}
